import { useContext, useEffect, useState } from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { CalculationContext } from './calculationContext';
import { formatPt } from './numberFormat';

const { FiInfo } = FiIcons;

function Calculator() {
  const [unit, setUnit] = useState('conventional');
  const [nitrogenType, setNitrogenType] = useState('bun');
  const [includeEthanol, setIncludeEthanol] = useState(false);
  const [na, setNa] = useState('');
  const [glucose, setGlucose] = useState('');
  const [nitrogen, setNitrogen] = useState('');
  const [ethanol, setEthanol] = useState('');
  const [measuredOsm, setMeasuredOsm] = useState('');
  const [result, setResult] = useState(null);
  const { setCalculation } = useContext(CalculationContext);

  useEffect(() => {
    setCalculation(result);
  }, [result, setCalculation]);

  useEffect(() => {
    const valNa = parseFloat(na);
    const valGlu = parseFloat(glucose);
    const valNit = parseFloat(nitrogen);
    const valEth = parseFloat(ethanol);
    const valMeas = parseFloat(measuredOsm);

    if ([valNa, valGlu, valNit].some(Number.isNaN)) {
      setResult(null);
      return;
    }

    let calcOsm = 0;
    let formula = '';

    if (unit === 'conventional') {
      const glucoseTerm = valGlu / 18;
      const nitrogenTerm = nitrogenType === 'bun' ? valNit / 2.8 : valNit / 6;
      calcOsm = (2 * valNa) + glucoseTerm + nitrogenTerm;
      formula = `2 × Na + Glicose/18 + ${nitrogenType === 'bun' ? 'BUN/2,8' : 'Ureia/6'}`;

      if (includeEthanol && !Number.isNaN(valEth)) {
        calcOsm += valEth / 4.6;
        formula += ' + Etanol/4,6';
      }
    } else {
      calcOsm = (2 * valNa) + valGlu + valNit;
      formula = `2 × Na + Glicose + ${nitrogenType === 'bun' ? 'BUN' : 'Ureia'}`;

      if (includeEthanol && !Number.isNaN(valEth)) {
        calcOsm += valEth;
        formula += ' + Etanol';
      }
    }

    setResult({
      osm: calcOsm,
      gap: Number.isNaN(valMeas) ? null : valMeas - calcOsm,
      formula,
      measured: Number.isNaN(valMeas) ? null : valMeas,
      ethanolContribution: includeEthanol && !Number.isNaN(valEth)
        ? (unit === 'conventional' ? valEth / 4.6 : valEth)
        : 0,
      values: {
        na: valNa,
        glucose: valGlu,
        nitrogen: valNit,
        ethanol: valEth,
      },
    });
  }, [unit, nitrogenType, includeEthanol, na, glucose, nitrogen, ethanol, measuredOsm]);

  return (
    <section id="calculadora" className="calculator-panel" aria-labelledby="calculator-title">
      <div className="calculator-heading">
        <h2 id="calculator-title">Calcule com seus resultados</h2>
        <p>Preencha sódio, glicose e BUN ou ureia para ver a estimativa.</p>
      </div>

      <div className="unit-switch" role="group" aria-label="Unidade de entrada">
        <button
          type="button"
          className={unit === 'conventional' ? 'unit-option active' : 'unit-option'}
          aria-pressed={unit === 'conventional'}
          onClick={() => setUnit('conventional')}
        >
          Convencional <span>mg/dL</span>
        </button>
        <button
          type="button"
          className={unit === 'si' ? 'unit-option active' : 'unit-option'}
          aria-pressed={unit === 'si'}
          onClick={() => setUnit('si')}
        >
          Sistema SI <span>mmol/L</span>
        </button>
      </div>

      <div className="input-grid">
        <label className="field">
          <span>Sódio (Na⁺)</span>
          <span className="input-wrap">
            <input
              type="number"
              inputMode="decimal"
              value={na}
              onChange={(event) => setNa(event.target.value)}
              placeholder="140"
            />
            <span className="unit-suffix">{unit === 'conventional' ? 'mEq/L' : 'mmol/L'}</span>
          </span>
        </label>

        <label className="field">
          <span>Glicose</span>
          <span className="input-wrap">
            <input
              type="number"
              inputMode="decimal"
              value={glucose}
              onChange={(event) => setGlucose(event.target.value)}
              placeholder={unit === 'conventional' ? '90' : '5,0'}
            />
            <span className="unit-suffix">{unit === 'conventional' ? 'mg/dL' : 'mmol/L'}</span>
          </span>
        </label>

        <label className="field nitrogen-field">
          <span className="field-label-row">
            <span>{nitrogenType === 'bun' ? 'Nitrogênio ureico (BUN)' : 'Ureia'}</span>
            <span className="nitrogen-toggle" role="group" aria-label="Tipo de resultado nitrogenado">
              <button
                type="button"
                className={nitrogenType === 'bun' ? 'selected' : ''}
                aria-pressed={nitrogenType === 'bun'}
                onClick={() => setNitrogenType('bun')}
              >
                BUN
              </button>
              <button
                type="button"
                className={nitrogenType === 'urea' ? 'selected' : ''}
                aria-pressed={nitrogenType === 'urea'}
                onClick={() => setNitrogenType('urea')}
              >
                Ureia
              </button>
            </span>
          </span>
          <span className="input-wrap">
            <input
              type="number"
              inputMode="decimal"
              value={nitrogen}
              onChange={(event) => setNitrogen(event.target.value)}
              placeholder={unit === 'conventional' ? '14' : '5,0'}
            />
            <span className="unit-suffix">{unit === 'conventional' ? 'mg/dL' : 'mmol/L'}</span>
          </span>
        </label>
      </div>

      <div className="optional-fields">
        <label className="check-row">
          <input
            type="checkbox"
            checked={includeEthanol}
            onChange={(event) => setIncludeEthanol(event.target.checked)}
          />
          <span>Incluir etanol na fórmula</span>
        </label>

        {includeEthanol && (
          <label className="field ethanol-field">
            <span>Etanol</span>
            <span className="input-wrap">
              <input
                type="number"
                inputMode="decimal"
                value={ethanol}
                onChange={(event) => setEthanol(event.target.value)}
                placeholder="Concentração"
              />
              <span className="unit-suffix">{unit === 'conventional' ? 'mg/dL' : 'mmol/L'}</span>
            </span>
          </label>
        )}

        <label className="field measured-field">
          <span>
            Osmolalidade medida <span className="optional-label">opcional</span>
          </span>
          <span className="input-wrap">
            <input
              type="number"
              inputMode="decimal"
              value={measuredOsm}
              onChange={(event) => setMeasuredOsm(event.target.value)}
              placeholder="290"
            />
            <span className="unit-suffix">mOsm/kg</span>
          </span>
        </label>
      </div>

      <div className="result-area" aria-live="polite">
        {result ? (
          <>
            <div className="result-main">
              <span className="result-label">Osmolaridade estimada</span>
              <strong>
                {formatPt(result.osm)} <small>mOsm/L</small>
              </strong>
              <p>
                {result.gap === null
                  ? 'Estimativa calculada com os valores informados.'
                  : `Diferença entre medida e estimativa: ${formatPt(result.gap)} mOsm/kg.`}
              </p>
            </div>
            <div className="result-details">
              {result.gap !== null && (
                <div className="gap-result">
                  <span>Gap osmolar</span>
                  <strong>
                    {formatPt(result.gap)} <small>mOsm/kg</small>
                  </strong>
                </div>
              )}
              <p><b>Fórmula:</b> {result.formula}</p>
              <p>
                <b>Valores usados:</b> Na {formatPt(result.values.na)}, glicose {formatPt(result.values.glucose)},
                {' '}{nitrogenType === 'bun' ? 'BUN' : 'ureia'} {formatPt(result.values.nitrogen)}
                {includeEthanol && !Number.isNaN(result.values.ethanol)
                  ? `, etanol ${formatPt(result.values.ethanol)}`
                  : ''}.
              </p>
              <p className="reference-note">
                <SafeIcon icon={FiInfo} aria-hidden="true" />
                O intervalo citado para o gap (frequentemente até 10 mOsm/kg) depende do laboratório e da equação.
              </p>
            </div>
          </>
        ) : (
          <p className="result-empty">
            A estimativa aparece aqui quando os três campos principais estiverem preenchidos.
          </p>
        )}
      </div>
    </section>
  );
}

export default Calculator;
